import { useEffect, type ReactNode } from "react";
import posthog from "posthog-js";
import { PostHogProvider as PHProvider } from "posthog-js/react";
import { registerGlobals } from "@/services/analytics";

// Standardized name is VITE_POSTHOG_KEY (matches AnimePup/CartoonTale);
// the legacy VITE_POSTHOG_API_KEY is kept as a fallback so existing
// deploys keep working until their env is updated.
const POSTHOG_KEY =
  import.meta.env.VITE_POSTHOG_KEY || import.meta.env.VITE_POSTHOG_API_KEY;
const POSTHOG_HOST =
  import.meta.env.VITE_POSTHOG_HOST || "https://us.i.posthog.com";

function getDeviceType(): "mobile" | "tablet" | "desktop" {
  const width = window.innerWidth;
  if (width < 768) return "mobile";
  if (width < 1024) return "tablet";
  return "desktop";
}

interface PostHogProviderProps {
  children: ReactNode;
}

export function PostHogProvider({ children }: PostHogProviderProps) {
  useEffect(() => {
    if (!POSTHOG_KEY || POSTHOG_KEY === "disabled") return;

    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      capture_pageview: true,
      capture_pageleave: true,
      capture_exceptions: true,
      persistence: "localStorage+cookie",
      loaded: (ph) => {
        registerGlobals({
          session_id: ph.get_session_id(),
          device_type: getDeviceType(),
          app_version: import.meta.env.VITE_APP_VERSION || "1.0.0",
        });
      },
    });
  }, []);

  if (!POSTHOG_KEY || POSTHOG_KEY === "disabled") {
    return <>{children}</>;
  }

  return <PHProvider client={posthog}>{children}</PHProvider>;
}
